import { Trans } from '@lingui/macro'
import { Percent } from '@uniswap/sdk-core'
import { useWeb3React } from '@web3-react/core'
import { ButtonPrimary } from 'components/Button'
import Card from 'components/Card'
import DoubleCurrencyLogo from 'components/DoubleLogo'
import { Skeleton } from 'components/Skeleton'
import { TransactionConfirmationContext } from 'contexts/TransactionConfirmationContext'
import { useCurrency } from 'hooks/Tokens'
import { useV3StakerContract } from 'hooks/useContract'
import { useV3StakerPositionsFromUser } from 'hooks/useV3StakerPositionsFromUser'
import { useCallback, useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTransactionAdder } from 'state/transactions/hooks'
import { TransactionType } from 'state/transactions/types'
import styled from 'styled-components/macro'
import { ThemedText } from 'theme/components/text'
import { opacify } from 'theme/utils'
import { PositionDetails } from 'types/position'
import { transactionErrorToUserReadableMessage } from 'utils/transactionErrorToUserReadableMessage'

const LMPositionsCard = styled(Card)`
  background-color: ${({ theme }) => theme.backgroundSurface};
  border: 1px solid ${({ theme }) => theme.backgroundOutline};
  padding: 1.25rem;
  border-radius: 16px;
  margin-top: 1rem;
`

const LMPositionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  width: 100%;
  margin-top: 0.75rem;
`

const LMPositionRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  border-radius: 12px;
  background-color: ${({ theme }) => theme.backgroundInteractive};
  border: 1px solid ${({ theme }) => theme.backgroundOutline};
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${({ theme }) => theme.backgroundModule};
  }
`

const PositionInfo = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const PositionDetailsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-top: 4px;
  gap: 4px;
`

const PositionTitle = styled(ThemedText.BodyPrimary)`
  font-weight: 500;
`

const PositionDetailsText = styled(ThemedText.BodySmall)`
  color: ${({ theme }) => theme.textSecondary};
  flex-grow: 1;
  margin-right: 8px;
`

const NoPositionsMessage = styled(ThemedText.BodySmall)`
  text-align: center;
  color: ${({ theme }) => theme.textSecondary};
  margin: 1rem 0;
`

const WithdrawButton = styled(ButtonPrimary)`
  height: 22px;
  padding: 0 6px;
  font-size: 11px;
  flex-shrink: 1;
  width: auto;
  border-radius: 6px;
  background-color: ${({ theme }) => theme.accentActionSoft};
  &:hover {
    background-color: ${({ theme }) => opacify(15, theme.accentAction)};
  }
`

// Helper function to prevent event propagation for the withdraw button
const stopPropagation = (e: React.MouseEvent<HTMLElement>) => {
  e.preventDefault()
  e.stopPropagation()
}

interface LMPositionItemProps {
  position: PositionDetails & {
    stakingInfo: {
      owner: string
      numberOfStakes: number
    }
  }
  onClick: () => void
}

function LMPositionItem({ position, onClick }: LMPositionItemProps) {
  const { account } = useWeb3React()
  const stakerContract = useV3StakerContract()
  const addTransaction = useTransactionAdder()
  const { transactionData, setTransactionData } = useContext(TransactionConfirmationContext)

  // Get token information
  const token0 = useCurrency(position.token0)
  const token1 = useCurrency(position.token1)

  // Format fee as percentage
  const feePercent = new Percent(position.fee, 1_000_000).toSignificant()

  // Check if position is deposited but not staked
  const isDeposited = position.stakingInfo.numberOfStakes === 0

  // Handle withdrawing the position
  const handleWithdraw = useCallback(async () => {
    if (!stakerContract || !account) return

    // Set transaction state to attempting
    setTransactionData({
      showConfirm: true,
      attempting: true,
      pendingText: `Withdrawing position #${position.tokenId.toString()}...`,
      hash: '',
      txError: '',
    })

    try {
      // Estimate gas for the transaction
      const estimatedGas = await stakerContract.estimateGas.withdrawToken(position.tokenId, account, [])

      // Call the withdrawToken function on the staker contract
      const tx = await stakerContract.withdrawToken(position.tokenId, account, [], {
        gasLimit: estimatedGas.mul(12).div(10), // Add 20% buffer to gas estimate
      })

      // Update transaction state with hash
      setTransactionData((transactionData) => ({
        ...transactionData,
        showConfirm: true,
        attempting: false,
        hash: tx.hash,
      }))

      // Add transaction to state for user feedback
      addTransaction(tx, {
        type: TransactionType.WITHDRAW_LIQUIDITY_STAKING,
        token0Address: position.token0,
        token1Address: position.token1,
      })

      await tx.wait()
    } catch (error) {
      console.error('Error withdrawing position:', error)

      // Update transaction state with error
      setTransactionData((transactionData) => ({
        ...transactionData,
        showConfirm: true,
        attempting: false,
        txError: transactionErrorToUserReadableMessage(error, 'An error occurred while withdrawing'),
      }))
    }
  }, [stakerContract, account, position, addTransaction, setTransactionData])

  return (
    <LMPositionRow onClick={onClick}>
      <PositionInfo>
        <PositionTitle>
          <DoubleCurrencyLogo currency0={token0 ?? undefined} currency1={token1 ?? undefined} size={20} margin />
          {token0?.symbol}/{token1?.symbol} ({feePercent}%)
        </PositionTitle>

        <PositionDetailsRow>
          <PositionDetailsText>
            <Trans>Token ID: {position.tokenId.toString()}</Trans>
            {position.stakingInfo.numberOfStakes > 0 && (
              <span>
                {' '}
                · <Trans>Stakes: {position.stakingInfo.numberOfStakes}</Trans>
              </span>
            )}
          </PositionDetailsText>

          {isDeposited && (
            <WithdrawButton
              onClick={(e) => {
                stopPropagation(e)
                handleWithdraw()
              }}
              disabled={
                transactionData.attempting && transactionData.pendingText?.includes(position.tokenId.toString())
              }
            >
              {transactionData.attempting && transactionData.pendingText?.includes(position.tokenId.toString()) ? (
                <Trans>Withdrawing...</Trans>
              ) : (
                <Trans>Withdraw</Trans>
              )}
            </WithdrawButton>
          )}
        </PositionDetailsRow>
      </PositionInfo>
    </LMPositionRow>
  )
}

export default function LMPositions() {
  const navigate = useNavigate()
  const { positions, loading } = useV3StakerPositionsFromUser()

  const handlePositionClick = useCallback(
    (tokenId: string) => {
      navigate(`/pool/${tokenId}`)
    },
    [navigate]
  )

  return (
    <LMPositionsCard>
      <ThemedText.SubHeaderSmall>
        <Trans>Your LM Positions</Trans>
      </ThemedText.SubHeaderSmall>

      {loading ? (
        <LMPositionsList>
          {[...Array(3)].map((_, index) => (
            <Skeleton key={index} width="100%" height={60} borderRadius={8} />
          ))}
        </LMPositionsList>
      ) : positions && positions.length > 0 ? (
        <LMPositionsList>
          {positions.map((position) => (
            <LMPositionItem
              key={position.tokenId.toString()}
              position={position}
              onClick={() => handlePositionClick(position.tokenId.toString())}
            />
          ))}
        </LMPositionsList>
      ) : (
        <NoPositionsMessage>
          <Trans>You have no LM positions</Trans>
        </NoPositionsMessage>
      )}
    </LMPositionsCard>
  )
}
